import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Clock, Users, Building } from 'lucide-react';

const ABOUT_BG = "https://images.unsplash.com/photo-1763616310284-68f51865dbea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3IlMjBibHVlJTIwc2t5JTIwbG93JTIwYW5nbGV8ZW58MHx8fHwxNzcwMjA2NjQwfDA&ixlib=rb-4.1.0&q=85";
const TEAM_CONTEXT = "/about-photo.png";

const values = [
  {
    icon: Clock,
    title: 'Economie de timp',
    description: 'Vă eliberăm de sarcinile administrative consumatoare de timp.'
  },
  {
    icon: Shield,
    title: 'Legalitate 100%',
    description: 'Toate serviciile respectă strict legislația în vigoare din România.'
  },
  {
    icon: Users,
    title: 'Experiență',
    description: 'Peste 12 ani de experiență în logistica și actele auto, cu mii de dosare prelucrate.'
  },
  {
    icon: Building,
    title: 'Parteneriate',
    description: 'Colaborări cu instituții pentru procesare rapidă.'
  }
];

const About = () => {
  return (
    <main className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 h-[400px]">
          <img 
            src={ABOUT_BG}
            alt="Modern office"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900/60" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="max-w-2xl text-white">
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-6" data-testid="about-title">
              Despre noi
            </h1>
            <p className="text-lg text-slate-200 leading-relaxed">
              AnDeni Time Solutions - partenerul dumneavoastră de încredere pentru 
              servicii administrative auto și documente oficiale.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Povestea noastră
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  AnDeni Time Solutions a fost fondată cu o misiune clară: să ajutăm 
                  românii să economisească timp prețios atunci când au de rezolvat 
                  actele auto și documentele oficiale.
                </p>
                <p>
                  Cu <strong>peste 12 ani de experiență</strong> în logistică și acte auto, 
                  am prelucrat <strong>mii de dosare</strong> și am acumulat o expertiză 
                  solidă în toate procedurile administrative. Fiecare dosar finalizat 
                  ne-a întărit convingerea că putem face lucrurile mai simplu pentru clienții noștri.
                </p>
                <p>
                  Știm cât de frustrant poate fi să pierzi ore întregi la ghișee, 
                  să te deplasezi de la o instituție la alta, să completezi formulare 
                  și să aștepți în cozi interminabile.
                </p>
                <p>
                  De aceea, am creat un serviciu complet care vă eliberează de 
                  aceste griji. Noi ne ocupăm de tot procesul administrativ, 
                  în timp ce dumneavoastră vă puteți concentra pe ce contează cu adevărat.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src={TEAM_CONTEXT}
                alt="Professional at work"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-blue-600 text-white p-6 rounded-xl shadow-lg hidden md:block">
                <p className="text-3xl font-bold">12+</p>
                <p className="text-sm">Ani de experiență</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Valorile noastre
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Principiile care ne ghidează în fiecare zi.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card 
                key={value.title}
                className="bg-white p-6 rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-300"
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-0">
                  <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                    <value.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl font-semibold mb-6">
              Misiunea noastră
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Ne-am propus să transformăm experiența administrativă a românilor, 
              oferind servicii rapide, legale și transparente. Credem că timpul 
              dumneavoastră este valoros și merită să fie investit în ce contează 
              cu adevărat pentru dumneavoastră.
            </p>
            <div className="inline-flex items-center gap-2 text-blue-400">
              <Shield className="h-5 w-5" />
              <span className="font-medium">Profesionalism și încredere garantate</span>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-slate-50 rounded-xl p-8 border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Disclaimer juridic
            </h3>
            <div className="text-sm text-slate-600 space-y-3">
              <p>
                AnDeni Time Solutions SRL oferă servicii de intermediere și asistență 
                administrativă pentru documente auto. Nu suntem o instituție de stat 
                și nu reprezentăm nicio autoritate publică.
              </p>
              <p>
                Serviciile noastre constau în completarea, depunerea și urmărirea 
                documentelor în numele clienților, pe baza documentelor și informațiilor 
                furnizate de aceștia.
              </p>
              <p>
                Prețurile afișate includ exclusiv serviciile de intermediere. 
                Taxele oficiale percepute de instituțiile de stat sunt separate 
                și vor fi comunicate înainte de procesarea dosarului.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
